import React from 'react';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { CircularProgress } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import pluralize from 'pluralize';
import unionBy from 'lodash/unionBy';
import clsx from 'clsx';

import useScrollToTop from 'hooks/useScrollToTop';
import MatchGigCard from './MatchGigCard';
import styles from 'styles/components/Discover.module.scss';

const progressStyle = { marginBottom: 20 };

const Matches = ({
  title,
  gigs,
  extraGigs,
  updateGig,
  isPerfectMatch,
  link,
  hasMore,
  isLoading,
  featuredGigs,
}) => {
  useScrollToTop(link);

  const hideSection = featuredGigs && gigs.length === 0 && !isLoading;

  if (hideSection) return null;

  return (
    <div
      className={clsx(styles.container, { [styles.perfectMatchContainer]: isPerfectMatch })}
      style={hasMore ? { paddingRight: 0 } : {}}
    >
      <div className={styles.header}>
        <h4>{title.toUpperCase()}</h4>

        {(hasMore || gigs.length + extraGigs.length >= 5) && (
          <Link href={link} passHref>
            <span className={styles.viewAll}>View all</span>
          </Link>
        )}
      </div>

      {isLoading && (
        <CircularProgress style={progressStyle} color={isPerfectMatch ? 'secondary' : 'primary'} />
      )}

      {gigs.length === 0 && extraGigs.length === 0 && !isLoading && (
        <Alert severity="info" className={isPerfectMatch ? 'main-alert' : ''}>
          No {pluralize(title.toLowerCase(), title.toLowerCase())} found
        </Alert>
      )}

      {(gigs.length > 0 || extraGigs.length > 0) && !isLoading && (
        <div className={styles.matches}>
          {unionBy(gigs, extraGigs, 'id')
            .slice(0, 5)
            .sort((a, b) => (featuredGigs ? 0 : new Date(b.created_at) - new Date(a.created_at)))
            .map(gig => (
              <MatchGigCard key={gig.id} gig={gig} updateGig={updateGig} />
            ))}
        </div>
      )}
    </div>
  );
};

Matches.propTypes = {
  title: PropTypes.string.isRequired,
  gigs: PropTypes.array.isRequired,
  extraGigs: PropTypes.array,
  updateGig: PropTypes.func.isRequired,
  isPerfectMatch: PropTypes.bool,
  link: PropTypes.string,
  hasMore: PropTypes.bool,
  isLoading: PropTypes.bool,
  featuredGigs: PropTypes.bool,
};

Matches.defaultProps = {
  extraGigs: [],
  isPerfectMatch: false,
  link: '',
  hasMore: false,
  isLoading: false,
  featuredGigs: false,
};

export default Matches;
